import { useContext, useEffect, useState } from 'react';
import { LoadingSpinner, StatusPill } from '../atoms';
import { useErrorHandlerContext, useOpeningElementsControllerContext, useUserLanguage } from '@/customHooks';
import { capitalizeFirstLetter } from '@/helpers';
import {
  GetOwnersQuery,
  OwnerFilterInput,
  useGetOwnersQuery
} from '@/graphql/generated';
import { SimpleOwnerProps } from '@/custom-types';
import { CreatePropertyContext } from '@/contexts';
import textsData from '@/textData/CreateContractsTexts.json';

type OwnerSimpleSelectionListProps = {
  filters: OwnerFilterInput;
  onDataCountChange?: (count: number) => void;
  selected: (owner: SimpleOwnerProps) => void;
}

type OwnerData = NonNullable<GetOwnersQuery['owners']>['items'][number]

const dataFlatter = (owners: OwnerData[] | undefined) => {
  if (!owners || owners.length === 0) return []

  return owners.map((owner) => {
    const city = owner.address.city?.name?.trim() || ''
    const state = owner.address.state?.name?.trim() || ''
    
    let address = ''
    if (city && state) {
      address = `${city}, ${state}`
    } else if (city) {
      address = city
    } else if (state) {
      address = state
    }
    
    return {
      firstName: owner.firstName,
      lastName: owner.lastName,
      isActive: owner.isActive,
      id: owner.user.id,
      address,
    }
  })
}

export const OwnerSimpleSelectionList = ({ filters, selected, onDataCountChange }: OwnerSimpleSelectionListProps) => {
  const userLanguage = useUserLanguage()
  const rootTexts = userLanguage === 'es' ? textsData.es : textsData.en
  const texts = rootTexts.ownerContract.ownersSelection

  const [selectedId, setSelectedId] = useState<string | null>(null)

  const createPropertyContext = useContext(CreatePropertyContext);

  const { state } = useOpeningElementsControllerContext()
  
  const { open: errorHandle } = useErrorHandlerContext()

  const { data, error, loading, refetch } = useGetOwnersQuery({
    variables: {
      filterInput: filters,
    },
    fetchPolicy: 'network-only',
  });
  
  if (error) errorHandle(error);
  
  const tableData = dataFlatter(data?.owners?.items)

  useEffect(() => {
    onDataCountChange?.(tableData.length);
  }, [tableData.length, onDataCountChange]);

  useEffect(() => {
    if (!state.isCreateOwnerFormModalOpen) refetch()
    
  }, [state.isCreateOwnerFormModalOpen, refetch])

  useEffect(() => {
    if (!data?.owners?.items) return
    const owner = data.owners.items.find((owner) => selectedId === owner.user.id)

    if (!owner) return
    
    selected(owner)

  }, [selectedId])

  useEffect(() => {
    if (!createPropertyContext) return;
    if (loading) createPropertyContext.setButtonDisabled()
    else createPropertyContext.setButtonEnabled()
  }, [createPropertyContext, loading])

  return (
    <div className="py-6">
      {loading ? (
        <div className="flex justify-center">
          <LoadingSpinner />
        </div>
      ) : (
        <div className="max-h-[60vh] overflow-y-auto rounded-xl grid grid-cols-[64px_2fr_1fr_2fr] content-start w-full min-w-[320px]">
          <div className="contents">
            <div className="font-bold text-left p-[15px] border-t border-b border-gray-500 bg-white sticky top-0 z-10" />
            <div className="font-bold text-left p-[15px] border-t border-b border-gray-500 bg-white sticky top-0 z-10">
              {texts.name}
            </div>
            <div className="font-bold text-left p-[15px] border-t border-b border-gray-500 bg-white sticky top-0 z-10">
              {texts.status}
            </div>
            <div className="font-bold text-left p-[15px] border-t border-b border-gray-500 bg-white sticky top-0 z-10">
              {texts.address}
            </div>
          </div>

          {tableData.map((data) => (
            <div
              key={data.id}
              className="contents group"
            >
              <div className="p-[15px] text-sm flex items-center border-b border-gray-200 group-odd:bg-white group-even:bg-[--color-background-2] min-h-[50px]">
                <input
                  type="radio"
                  checked={selectedId === data.id}
                  onChange={() => setSelectedId(data.id)}
                  disabled={!data.isActive}
                  className="cursor-pointer"
                />
              </div>
              <div className="p-[15px] text-sm flex items-center border-b border-gray-200 group-odd:bg-white group-even:bg-[--color-background-2] min-h-[50px]">
                {`${capitalizeFirstLetter(data.firstName)} ${capitalizeFirstLetter(
                  data.lastName,
                )}`}
              </div>
              <div className="p-[15px] text-sm flex items-center border-b border-gray-200 group-odd:bg-white group-even:bg-[--color-background-2] min-h-[50px]">
                <StatusPill isActive={data.isActive} />
              </div>
              <div className="p-[15px] text-sm flex items-center border-b border-gray-200 group-odd:bg-white group-even:bg-[--color-background-2] min-h-[50px]">
                {data.address}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
